import React from 'react';
import { PlaybookAdherence, getCoveragePctClasses } from '../../adherence/types';
import SummarySectionCard from './SummarySectionCard';

interface QualifyingQuestionsSummaryCardProps {
  playbook?: PlaybookAdherence;
  discoveryPhaseTimestamp?: string;
  onViewDetails: () => void;
  onViewInTimeline?: () => void;
}

const QualifyingQuestionsSummaryCard: React.FC<QualifyingQuestionsSummaryCardProps> = ({
  playbook,
  discoveryPhaseTimestamp,
  onViewDetails,
  onViewInTimeline,
}) => {
  const hasGeneric = !!playbook?.question_generic;
  const hasPlaybook = playbook?.was_used && playbook?.question_coverage;
  const coveragePct = playbook?.question_coverage?.coverage_pct;
  const genericScore = playbook?.question_generic?.score;

  const displayScore = hasPlaybook && coveragePct != null ? coveragePct : genericScore;

  const badgeColor =
    displayScore == null
      ? 'bg-gray-100 text-gray-500 border border-gray-200'
      : displayScore >= 70
      ? 'bg-green-100 text-green-700 border border-green-200'
      : displayScore >= 40
      ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
      : 'bg-red-100 text-red-700 border border-red-200';

  const statusBadge = displayScore != null ? (
    <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${badgeColor}`}>{displayScore}%</span>
  ) : undefined;

  const coachingText = playbook?.question_generic?.coaching;

  const missingMsg =
    !playbook
      ? 'Qualifying questions analysis requires re-analysis of this session.'
      : !hasGeneric
      ? 'Re-analyze this session to see generic qualifying questions analysis.'
      : undefined;

  const resultContent = (
    <div className="space-y-3">
      {hasPlaybook && playbook.question_coverage && (
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold text-blue-700 uppercase tracking-wider">Playbook Coverage</p>
            <span className="text-xs font-bold text-blue-800">
              {playbook.question_coverage.total_asked}/{playbook.question_coverage.total_available} questions
            </span>
          </div>
          <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${getCoveragePctClasses(playbook.question_coverage.coverage_pct)}`}
              style={{ width: `${playbook.question_coverage.coverage_pct}%` }}
            />
          </div>
          {playbook.question_coverage.question_quality_summary && (
            <p className="text-xs text-blue-900 mt-2">{playbook.question_coverage.question_quality_summary}</p>
          )}
        </div>
      )}
      {playbook?.question_generic && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Generic Analysis</p>
            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${badgeColor}`}>{genericScore}%</span>
          </div>
          <p className="text-xs text-gray-700">{playbook.question_generic.summary}</p>
          {(playbook.question_generic.what_worked || playbook.question_generic.what_missed) && (
            <div className="mt-2 space-y-1 pt-2 border-t border-gray-200">
              {playbook.question_generic.what_worked && (
                <p className="text-[11px] text-green-700">
                  <span className="font-semibold">Worked: </span>{playbook.question_generic.what_worked}
                </p>
              )}
              {playbook.question_generic.what_missed && (
                <p className="text-[11px] text-red-600">
                  <span className="font-semibold">Missed: </span>{playbook.question_generic.what_missed}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <SummarySectionCard
      title="Qualifying Questions"
      icon={<span>&#10067;</span>}
      statusBadge={statusBadge}
      resultContent={resultContent}
      coachingItems={coachingText ? [coachingText] : undefined}
      sectionKey="qualifying"
      onViewDetails={onViewDetails}
      onViewInTimeline={discoveryPhaseTimestamp ? onViewInTimeline : undefined}
      missingDataMessage={missingMsg}
    />
  );
};

export default QualifyingQuestionsSummaryCard;
